import { useState, useEffect } from 'react'
import { Link, useSearchParams, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/primitives'
import { Badge } from '@/components/primitives/Badge'
import { toast } from 'sonner'
import {
  EnvelopeSimple,
  Lock,
  Eye,
  EyeSlash,
  User,
  Buildings,
  Gift,
  Check,
} from '@phosphor-icons/react'
import { PinDayLogo } from '@/components/ui/PinDayLogo'
import { useInviteApi } from '@/hooks/useApi'

export function RegisterPage() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const { locale } = useParams<{ locale: string }>()
  const [name, setName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const { register } = useAuth()
  const { validateInvite } = useInviteApi()

  // Invite code state
  const [inviteCode, setInviteCode] = useState('')
  const [inviteInfo, setInviteInfo] = useState<{
    planId: string
    durationMonths: number
    description?: string
  } | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [isValidatingInvite, setIsValidatingInvite] = useState(false)
  const selectedPlan = searchParams.get('plan')
  const selectedCurrency = searchParams.get('currency')

  // Check for invite code in URL on mount
  useEffect(() => {
    const inviteFromUrl = searchParams.get('invite')
    if (inviteFromUrl) {
      setInviteCode(inviteFromUrl)
      handleValidateInvite(inviteFromUrl)
    }
  }, [searchParams])

  const handleValidateInvite = async (code: string) => {
    if (!code.trim()) {
      setInviteInfo(null)
      setInviteError(null)
      return
    }

    setIsValidatingInvite(true)
    setInviteError(null)

    try {
      const result = await validateInvite(code.trim())
      if (result.valid && result.invite) {
        setInviteInfo(result.invite)
        setInviteError(null)
      } else {
        setInviteInfo(null)
        setInviteError(result.reason || 'INVALID_CODE')
      }
    } catch {
      setInviteInfo(null)
      setInviteError('VALIDATION_ERROR')
    } finally {
      setIsValidatingInvite(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name || !businessName || !email || !password || !confirmPassword) {
      toast.error(t('errors.required'))
      return
    }

    if (password.length < 6) {
      toast.error(t('errors.weakPassword'))
      return
    }

    if (password !== confirmPassword) {
      toast.error(t('common:validation.passwordMatch'))
      return
    }

    setIsLoading(true)

    try {
      await register({
        email,
        password,
        name,
        businessName,
        locale: locale === 'pt' ? 'pt_BR' : locale === 'es' ? 'es' : 'en',
        currency: selectedCurrency === 'BRL' || selectedCurrency === 'USD' || selectedCurrency === 'EUR' ? selectedCurrency : undefined,
        inviteCode: inviteInfo ? inviteCode : undefined,
      })
      
      if (inviteInfo) {
        toast.success(t('register.betaSuccess', { months: inviteInfo.durationMonths, plan: inviteInfo.planId.toUpperCase() }))
      } else {
        toast.success(t('verification.subtitle'))
      }

      setSubmittedEmail(email)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('common:errors.generic')
      if (errorMessage.includes('already exists')) {
        toast.error(t('errors.emailInUse'))
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (submittedEmail) {
    return (
      <div className="min-h-screen bg-[var(--pd-bg)] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex justify-center">
              <PinDayLogo size="xl" />
            </Link>
          </div>

          <Card>
            <div className="p-8 text-center space-y-5">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--pd-primary)]/10 text-[var(--pd-primary)]">
                <EnvelopeSimple size={32} weight="fill" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[var(--pd-foreground)]">
                  {t('verification.title')}
                </h1>
                <p className="mt-2 text-sm text-[var(--pd-foreground)]/70">
                  {t('verification.subtitle')}
                </p>
                <p className="mt-3 break-all text-sm font-medium text-[var(--pd-foreground)]">
                  {submittedEmail}
                </p>
              </div>
              <p className="text-sm text-[var(--pd-foreground)]/60">
                {t('register.verifyBeforeAccount')}
              </p>
              <Link to="/login" className="block">
                <Button className="w-full">
                  {t('register.signIn')}
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--pd-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center">
            <PinDayLogo size="xl" />
          </Link>
          <p className="text-[var(--pd-foreground)]/60 mt-2">
            {t('register.subtitle')}
          </p>
        </div>

        {/* Beta Invite Banner */}
        {!inviteInfo && selectedPlan && (
          <div className="mb-6 rounded-lg border border-[var(--pd-primary)]/20 bg-[var(--pd-primary)]/5 p-4">
            <p className="text-sm font-semibold text-[var(--pd-foreground)]">
              {t('register.planIntent', { defaultValue: 'Você está começando com intenção de plano {{plan}}', plan: selectedPlan.toUpperCase() })}
            </p>
            {selectedCurrency && (
              <p className="mt-1 text-xs text-[var(--pd-foreground)]/60">
                {t('register.currencyIntent', { defaultValue: 'Moeda preferida: {{currency}}', currency: selectedCurrency })}
              </p>
            )}
          </div>
        )}

        {inviteInfo && (
          <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-purple-500/20">
                <Gift size={20} className="text-purple-600 dark:text-purple-400" weight="fill" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-[var(--pd-foreground)]">
                    {t('register.betaInvite')}
                  </span>
                  <Badge variant="success" size="sm">
                    <Check size={12} weight="bold" className="mr-1" />
                    {t('register.valid')}
                  </Badge>
                </div>
                <p className="text-sm text-[var(--pd-foreground)]/70">
                  {t('register.betaDescription', { months: inviteInfo.durationMonths, plan: inviteInfo.planId.toUpperCase() })}
                </p>
                {inviteInfo.description && (
                  <p className="text-xs text-[var(--pd-foreground)]/50 mt-1">
                    {inviteInfo.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Card de Registro */}
        <Card>
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="name">{t('register.name')}</Label>
                <div className="relative">
                  <User
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="name"
                    type="text"
                    placeholder={t('register.namePlaceholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    autoComplete="name"
                    required
                  />
                </div>
              </div>

              {/* Nome do Negócio */}
              <div className="space-y-2">
                <Label htmlFor="businessName">{t('settings:profileSection.businessName', { defaultValue: 'Business name' })}</Label>
                <div className="relative">
                  <Buildings
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="businessName"
                    type="text"
                    placeholder="Studio Fotografia"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    className="pl-10"
                    autoComplete="organization"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">{t('register.email')}</Label>
                <div className="relative">
                  <EnvelopeSimple
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('register.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">{t('register.password')}</Label>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('register.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40 hover:text-[var(--pd-foreground)] transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-[var(--pd-foreground)]/50">
                  {t('common:validation.minLength', { min: 6 })}
                </p>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('register.confirmPassword')}</Label>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                  />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('register.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40 hover:text-[var(--pd-foreground)] transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeSlash size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
              </div>

              {/* Invite Code (optional, if not already provided via URL) */}
              {!inviteInfo && (
                <div className="space-y-2">
                  <Label htmlFor="inviteCode">
                    {t('register.inviteCode', 'Código de Convite')}
                    <span className="text-[var(--pd-foreground)]/50 ml-1">
                      ({t('common:optional', 'opcional')})
                    </span>
                  </Label>
                  <div className="relative">
                    <Gift
                      size={20}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pd-foreground)]/40"
                    />
                    <Input
                      id="inviteCode"
                      type="text"
                      placeholder={t('register.inviteCodePlaceholder', 'BETA-XXXXXX')}
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      onBlur={(e) => handleValidateInvite(e.target.value)}
                      className="pl-10 font-mono"
                      autoComplete="off"
                    />
                    {isValidatingInvite && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--pd-foreground)]/50">
                        {t('common:validating', 'Validando...')}
                      </span>
                    )}
                  </div>
                  {inviteError && (
                    <p className="text-xs text-red-500">
                      {inviteError === 'INVALID_CODE' && t('register.inviteErrors.invalid', 'Código inválido')}
                      {inviteError === 'CODE_INACTIVE' && t('register.inviteErrors.inactive', 'Código desativado')}
                      {inviteError === 'CODE_EXPIRED' && t('register.inviteErrors.expired', 'Código expirado')}
                      {inviteError === 'CODE_EXHAUSTED' && t('register.inviteErrors.exhausted', 'Código atingiu limite de uso')}
                      {!['INVALID_CODE', 'CODE_INACTIVE', 'CODE_EXPIRED', 'CODE_EXHAUSTED'].includes(inviteError) && t('register.inviteErrors.generic', 'Erro ao validar código')}
                    </p>
                  )}
                </div>
              )}

              {/* Termos */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  className="rounded border-[var(--pd-border)] text-[var(--pd-primary)] mt-1"
                  required
                />
                <label
                  htmlFor="terms"
                  className="text-sm text-[var(--pd-foreground)]/60"
                >
                  {t('register.acceptTerms')}{' '}
                  <a
                    href="#"
                    className="text-[var(--pd-primary)] hover:text-[var(--pd-primary)]/80 transition-colors"
                  >
                    {t('register.termsOfService')}
                  </a>{' '}
                  {t('register.and')}{' '}
                  <a
                    href="#"
                    className="text-[var(--pd-primary)] hover:text-[var(--pd-primary)]/80 transition-colors"
                  >
                    {t('register.privacyPolicy')}
                  </a>
                </label>
              </div>

              {/* Botão de Registro */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[var(--pd-primary)] hover:bg-[var(--pd-primary)]/90 text-white py-6 rounded-xl font-semibold text-base shadow-lg transition-all duration-200"
              >
                {isLoading ? t('register.submitting') : t('register.submit')}
              </Button>
            </form>

            {/* Divisor */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--pd-border)]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[var(--pd-card)] text-[var(--pd-foreground)]/60">
                  {t('register.or')}
                </span>
              </div>
            </div>

            {/* Login */}
            <div className="text-center text-sm text-[var(--pd-foreground)]/60">
              {t('register.hasAccount')}{' '}
              <Link
                to="/login"
                className="text-[var(--pd-primary)] hover:text-[var(--pd-primary)]/80 font-semibold transition-colors"
              >
                {t('register.signIn')}
              </Link>
            </div>
          </div>
        </Card>

        {/* Link para voltar */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-[var(--pd-foreground)]/60 hover:text-[var(--pd-foreground)] transition-colors"
          >
            ← {t('common:actions.back')}
          </Link>
        </div>
      </div>
    </div>
  )
}
